/**
 * GroupSessionPage
 *
 * Main page component for a Group Prioritization session.
 * Routes to the correct child view based on groupSessionStatus.
 *
 * Route: /groups/:sessionId
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { GroupDecisionFramingPanel, useGroupPageToast } from './GroupDecisionFramingPanel';
import { GroupSessionStatusPill } from './GroupSessionStatusPill';
import { GroupSetupView } from './GroupSetupView';
import { GroupScoringView } from './GroupScoringView';
import { GroupResultsView } from './GroupResultsView';
import { PreS6ResultsPanel } from './PreS6ResultsPanel';
import type { User } from '@supabase/supabase-js';

// ─── Inline "Waiting" view for non-host members during SETUP ────────────────

function WaitingForHost() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--rule-bright)',
          borderTopColor: 'var(--mustard)',
          borderRadius: '50%',
          animation: 'pp-spin 1s linear infinite',
        }}
      />
      <p
        style={{
          fontFamily: 'var(--sans)',
          fontSize: '16px',
          color: 'var(--text-sec)',
          margin: 0,
          maxWidth: '320px',
          lineHeight: 1.5,
        }}
      >
        Waiting for the host to open this decision session…
      </p>
    </div>
  );
}

// ─── Open view — all members see options + criteria while waiting ────────────

function GroupOpenView({ isHost, sessionId }: { isHost: boolean; sessionId: string }) {
  const { options, criteria, currentSession, startScoring } = useGroupSessionStore();
  const toast = useGroupPageToast();
  const [starting, setStarting] = React.useState(false);
  const preS6Snapshot = currentSession?.preS6Snapshot;

  const handleStartScoring = React.useCallback(async () => {
    if (starting) return;
    setStarting(true);
    try {
      await startScoring(sessionId);
    } catch {
      toast.show('Could not start scoring. Please try again.');
    } finally {
      setStarting(false);
    }
  }, [starting, startScoring, sessionId, toast]);

  return (
    <div style={{ padding: '0 0 40px' }}>
      {toast.message && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            border: '1px solid var(--rule-bright)',
            borderRadius: '8px',
            padding: '10px 20px',
            fontFamily: 'var(--sans)',
            fontSize: '14px',
            color: 'var(--text-pri)',
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
        >
          {toast.message}
        </div>
      )}

      {isHost && <GroupDecisionFramingPanel sessionId={sessionId} toast={toast} />}

      {/* Banner */}
      <div
        style={{
          background: 'var(--mustard-dim)',
          border: '1px solid var(--mustard)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
          <span style={{ fontSize: '18px' }}>⏳</span>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--mustard)' }}>
            {isHost
              ? 'Session is open. Start scoring when everyone has joined.'
              : 'Scoring will begin when the host starts the session.'}
          </span>
        </div>
        {isHost && (
          <button
            type="button"
            onClick={() => void handleStartScoring()}
            disabled={starting || options.length === 0 || criteria.length === 0}
            style={{
              background: starting ? 'var(--rule-bright)' : 'var(--mustard)',
              color: '#0B0F19',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              fontWeight: 700,
              padding: '9px 18px',
              cursor: (starting || options.length === 0 || criteria.length === 0) ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              opacity: (starting || options.length === 0 || criteria.length === 0) ? 0.5 : 1,
              transition: 'opacity 0.15s',
            }}
            title={options.length === 0 ? 'Add options before starting' : criteria.length === 0 ? 'Add criteria before starting' : undefined}
          >
            {starting ? 'Starting…' : '▶ Start Scoring'}
          </button>
        )}
      </div>

      {/* Session description (guests — host edits this in Decision framing above) */}
      {!isHost && currentSession?.description && (
        <p
          style={{
            fontFamily: 'var(--sans)',
            fontSize: '14px',
            color: 'var(--text-sec)',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}
        >
          {currentSession.description}
        </p>
      )}

      {/* Pre-S6 System Recommendation (reference baseline for group deliberation) */}
      {preS6Snapshot && (
        <PreS6ResultsPanel
          snapshot={preS6Snapshot}
          defaultExpanded={isHost}
        />
      )}

      {/* Options */}
      <Section label={`Options (${options.length})`}>
        {options.length === 0 ? (
          <EmptyHint>No options added yet.</EmptyHint>
        ) : (
          options.map((opt) => (
            <div key={opt.id} style={optionCardStyle}>
              <span style={optionTitleStyle}>{opt.title}</span>
              {opt.description && (
                <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-ter)' }}>
                  {opt.description}
                </span>
              )}
            </div>
          ))
        )}
      </Section>

      {/* Criteria */}
      <Section label={`What Matters (${criteria.length} criteria)`}>
        {criteria.length === 0 ? (
          <EmptyHint>No criteria defined yet.</EmptyHint>
        ) : (
          <CriteriaList criteria={criteria} />
        )}
      </Section>
    </div>
  );
}

// ─── Shared small sub-components ─────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-ter)',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontFamily: 'var(--sans)',
        fontSize: '13px',
        color: 'var(--text-ter)',
        fontStyle: 'italic',
        margin: 0,
        padding: '12px 0',
      }}
    >
      {children}
    </p>
  );
}

function CriteriaList({ criteria }: { criteria: import('../../types/prioritization/groupSession').GroupCriterion[] }) {
  const totalWeight = criteria.reduce((s, c) => s + c.weight, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {criteria.map((c) => {
        const pct = Math.round((c.weight / totalWeight) * 100);
        return (
          <div key={c.id} style={criterionRowStyle}>
            <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--text-pri)', flex: 1 }}>
              {c.title}
            </span>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '12px',
                color: 'var(--mustard)',
                background: 'var(--mustard-dim)',
                padding: '2px 8px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

const optionCardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--rule)',
  borderRadius: '8px',
  padding: '12px 14px',
  marginBottom: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const optionTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--text-pri)',
};

const criterionRowStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--rule)',
  borderRadius: '8px',
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

// ─── Page shell ───────────────────────────────────────────────────────────────

export function GroupSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const {
    currentSession,
    isLoading,
    error,
    loadGroupSession,
    subscribeToGroupSession,
    unsubscribeFromGroupSession,
  } = useGroupSessionStore();

  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Resolve Supabase auth user once
  useEffect(() => {
    const supabase = getSupabaseClient();
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUser(data.user);
    });
  }, []);

  // Load + subscribe on mount
  useEffect(() => {
    if (!sessionId) return;

    async function init() {
      await loadGroupSession(sessionId!);
      const workspaceId = useGroupSessionStore.getState().currentSession?.workspaceId ?? '';
      await subscribeToGroupSession(sessionId!, workspaceId);
    }

    void init();

    return () => {
      unsubscribeFromGroupSession(sessionId!);
    };
  }, [sessionId, loadGroupSession, subscribeToGroupSession, unsubscribeFromGroupSession]);

  const isHost = Boolean(
    currentSession && currentUser && currentSession.facilitatorId === currentUser.id,
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading && !currentSession) {
    return (
      <PageShell>
        <div style={centeredStyle}>
          <Spinner />
          <p style={mutedLabel}>Loading session…</p>
        </div>
      </PageShell>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <PageShell>
        <div style={centeredStyle}>
          <div
            style={{
              background: 'var(--coral-dim)',
              border: '1px solid var(--coral)',
              borderRadius: '8px',
              padding: '16px 20px',
              maxWidth: '400px',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '15px',
                color: 'var(--coral)',
                margin: 0,
              }}
            >
              {error}
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── No session yet ─────────────────────────────────────────────────────────
  if (!currentSession || !sessionId) {
    return (
      <PageShell>
        <div style={centeredStyle}>
          <p style={mutedLabel}>Session not found.</p>
        </div>
      </PageShell>
    );
  }

  // ── Route by status ────────────────────────────────────────────────────────
  const { groupSessionStatus } = currentSession;

  return (
    <>
      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes pp-spin { to { transform: rotate(360deg); } }
        @keyframes pp-fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      `}</style>

      <PageShell>
        {!((groupSessionStatus === 'SETUP' || groupSessionStatus === 'OPEN') && isHost) && (
          <div style={headerStyle}>
            <h1 style={titleStyle}>{currentSession.title}</h1>
            <GroupSessionStatusPill status={groupSessionStatus} />
          </div>
        )}

        {/* Content per status */}
        {groupSessionStatus === 'SETUP' && (
          isHost
            ? <GroupSetupView sessionId={sessionId} isHost={true} />
            : <WaitingForHost />
        )}

        {groupSessionStatus === 'OPEN' && (
          <GroupOpenView isHost={isHost} sessionId={sessionId} />
        )}

        {groupSessionStatus === 'SCORING' && (
          <GroupScoringView sessionId={sessionId} isHost={isHost} />
        )}

        {groupSessionStatus === 'REVEALING' && (
          <GroupResultsView sessionId={sessionId} isHost={isHost} revealMode={true} />
        )}

        {groupSessionStatus === 'DECIDED' && (
          <>
            {/* Leadership Review notice — Group Recommendation is not yet the Final Ranking */}
            {isHost && (
              <div
                style={{
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--rule-bright)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}
              >
                <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>⟳</span>
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--sans)',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'var(--text-pri)',
                      marginBottom: '4px',
                    }}
                  >
                    Awaiting Leadership Review
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--sans)',
                      fontSize: '13px',
                      color: 'var(--text-sec)',
                      lineHeight: 1.5,
                    }}
                  >
                    The Group Recommendation has been recorded. Submit it for leadership review to
                    produce the Decision of Record and Final Ranking.
                  </div>
                </div>
              </div>
            )}
            <GroupResultsView sessionId={sessionId} isHost={isHost} revealMode={false} />
          </>
        )}
      </PageShell>
    </>
  );
}

// ─── Layout primitives ────────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 'min(720px, 100%)',
          padding: '32px 0 80px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div
      style={{
        width: '36px',
        height: '36px',
        border: '3px solid var(--rule-bright)',
        borderTopColor: 'var(--mustard)',
        borderRadius: '50%',
        animation: 'pp-spin 1s linear infinite',
        marginBottom: '12px',
      }}
    />
  );
}

const centeredStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
};

const mutedLabel: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  color: 'var(--text-ter)',
  margin: 0,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '28px',
  flexWrap: 'wrap',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--serif)',
  fontSize: '22px',
  fontWeight: 600,
  color: 'var(--text-pri)',
  margin: 0,
  lineHeight: 1.3,
  flex: 1,
};

